import { ReactElement, useCallback } from 'react';
import { Stack, Typography, Tooltip } from '@mui/material';
import {
  faUserClock as AssignDriverIcon,
  faPenToSquare as EditDriverIcon,
} from '@fortawesome/free-solid-svg-icons';
import { useSnackbar } from 'notistack';
import classNames from 'classnames';

import { useToggle } from '~hooks/useToggle';
import { DriverCompanyBase } from '~services/driver/types';
import UserInfo from '~components/UserInfo';
import DialogAssignDriver from '~components/DialogAssignDriver';
import DialogDriverCompanyInvite from '~components/DialogDriverCompanyInvite';

import { AssistantGenerateRoutesDialogInfoAlert } from '../../styles';

import { AssistantGenerateRoutesDialogPreAsignedDriverActionProps } from './types';
import {
  AssistantGenerateRoutesDialogPreAsignedDriverActionButton,
  AssistantGenerateRoutesDialogPreAsignedDriverActionIcon,
  AssistantGenerateRoutesDialogPreAsignedDriverActionIconButton,
} from './styles';

const INFO_TEXT =
  'En caso de pre-assignar un chofer, se notificará automáticamente al generar la ruta.';

const AssistantGenerateRoutesDialogPreAsignedDriverAction = ({
  currentDriver,
  onAssigned,
  disabled,
}: AssistantGenerateRoutesDialogPreAsignedDriverActionProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const [openDialogAssignedDriver, toggleOpenDialogAssignedDriver] =
    useToggle(false);

  const [openDialogInviteDriver, toogleOpenDialogInviteDriver] =
    useToggle(false);

  const handleOpenDialogInviteDriver = useCallback(() => {
    toggleOpenDialogAssignedDriver(false);

    setTimeout(() => {
      toogleOpenDialogInviteDriver(true);
    }, 300);
  }, [toggleOpenDialogAssignedDriver, toogleOpenDialogInviteDriver]);

  const handleAssignedDriver = useCallback(
    (currentAssignedDriver: DriverCompanyBase) => {
      toggleOpenDialogAssignedDriver(false);

      setTimeout(() => {
        onAssigned(currentAssignedDriver);

        enqueueSnackbar('Chofer cargado correctamente', {
          variant: 'success',
        });
      }, 200);
    },
    [onAssigned, enqueueSnackbar, toggleOpenDialogAssignedDriver],
  );

  return (
    <Stack spacing={1}>
      <Stack
        spacing={1}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="subtitle2" fontWeight="bold">
          CHOFER PRE-ASIGNADO
        </Typography>

        <Stack spacing={1} direction="row">
          {!currentDriver && (
            <AssistantGenerateRoutesDialogPreAsignedDriverActionButton
              color="primary"
              variant="outlined"
              size="small"
              className={classNames({ pulse: !disabled })}
              startIcon={
                <AssistantGenerateRoutesDialogPreAsignedDriverActionIcon
                  icon={AssignDriverIcon}
                />
              }
              onClick={() => toggleOpenDialogAssignedDriver(true)}
              disabled={disabled}
            >
              Asignar Chofer
            </AssistantGenerateRoutesDialogPreAsignedDriverActionButton>
          )}

          {currentDriver && (
            <Stack direction="row" spacing={2}>
              <UserInfo
                name={currentDriver.displayName}
                urlAvatar={currentDriver.avatar ?? undefined}
              />

              <Tooltip title="Modificar chofer" arrow>
                <AssistantGenerateRoutesDialogPreAsignedDriverActionIconButton
                  variant="contained"
                  color="secondary"
                  onClick={() => toggleOpenDialogAssignedDriver(true)}
                >
                  <AssistantGenerateRoutesDialogPreAsignedDriverActionIcon
                    icon={EditDriverIcon}
                  />
                </AssistantGenerateRoutesDialogPreAsignedDriverActionIconButton>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </Stack>

      <AssistantGenerateRoutesDialogInfoAlert text={INFO_TEXT} />

      {openDialogAssignedDriver && (
        <DialogAssignDriver
          open={openDialogAssignedDriver}
          onClose={() => toggleOpenDialogAssignedDriver(false)}
          onSubmit={handleAssignedDriver}
          handleOpenDialogInviteDriver={handleOpenDialogInviteDriver}
          typeFetchList="General"
          excludeIsAvailableOptions
        />
      )}

      <DialogDriverCompanyInvite
        open={openDialogInviteDriver}
        onClose={() => toogleOpenDialogInviteDriver(false)}
      />
    </Stack>
  );
};

export default AssistantGenerateRoutesDialogPreAsignedDriverAction;
